// src/components/HomePage.tsx

import React from 'react';
import { Button } from './ui/button.tsx';
import { ImageWithFallback } from './resim/ImageWithFallback.tsx';
import { useNavigate } from 'react-router-dom';
import { Users, Handshake, Award, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

// Bilgisayarındaki fotoğrafı içeri aktarıyoruz
import homepageImage from '../assets/homepage.png';

export function HomePage() {
    const isLoggedIn = !!localStorage.getItem('access');
    const navigate = useNavigate();

    return (
        <div className="space-y-24 pb-20">
            
            {/* --- YEPYENİ MODERN HERO SECTION (İki Kolonlu Ferah Tasarım) --- */}
            <section className="container mx-auto px-6 md:px-12 pt-12 lg:pt-20">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
                    
                    {/* SOL TARAF: Güçlü Tipografi ve Metinler */}
                    <motion.div 
                        initial={{ opacity: 0, x: -30 }} 
                        animate={{ opacity: 1, x: 0 }} 
                        transition={{ duration: 0.6 }}
                        className="lg:w-1/2 flex flex-col items-center text-center lg:items-start lg:text-left"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 text-[#A8322D] text-sm font-semibold mb-6 border border-red-100">
                            <Award className="w-4 h-4" />
                            <span>Süleyman Demirel Anadolu Lisesi</span>
                        </div>
                        
                        <h1 className="mb-6 text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.15]">
                            Mezunlar Topluluğuna <br className="hidden lg:block"/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A8322D] to-red-500">
                                Hoş Geldiniz
                            </span>
                        </h1>
                        
                        <p className="mb-8 text-lg text-slate-600 leading-relaxed max-w-lg">
                            Geçmişin bağlarını koparmadan, geleceğe birlikte yürüyoruz. Lise yıllarındaki dayanışmayı profesyonel hayata taşıyın ve bu büyük ailenin bir parçası olun.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                            {!isLoggedIn ? (
                                <Button 
                                    size="lg" 
                                    className="bg-[#A8322D] text-white hover:bg-[#8a2723] rounded-full px-8 py-6 shadow-xl shadow-red-900/20 transition-all hover:-translate-y-1 group font-bold text-base"
                                    onClick={() => navigate('/uyelik?tab=basvuru')}
                                >
                                    Aramıza Katıl 
                                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                                </Button>
                            ) : (
                                <Button 
                                    size="lg" 
                                    className="bg-[#A8322D] text-white hover:bg-[#8a2723] rounded-full px-8 py-6 shadow-xl shadow-red-900/20 transition-all hover:-translate-y-1 font-bold text-base"
                                    onClick={() => navigate('/profil')}
                                >
                                    Profilime Git
                                </Button>
                            )}
                            
                            <Button 
                                variant="outline"
                                size="lg" 
                                className="rounded-full px-8 py-6 border-slate-200 text-slate-700 hover:bg-slate-50 transition-all font-bold text-base bg-white"
                                onClick={() => {
                                    document.getElementById('hakkimizda')?.scrollIntoView({ behavior: 'smooth' });
                                }}
                            >
                                Daha Fazla Bilgi
                            </Button>
                        </div>
                    </motion.div>

                    {/* SAĞ TARAF: Şık Görsel Sunumu */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95, x: 30 }} 
                        animate={{ opacity: 1, scale: 1, x: 0 }} 
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="lg:w-1/2 relative w-full max-w-lg lg:max-w-none mx-auto mt-10 lg:mt-0"
                    >
                        {/* Arka plan dekoratif eğimli kutu */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-red-100 to-orange-50 rounded-[3rem] transform rotate-3 scale-105 -z-10"></div>
                        
                        {/* Ana Fotoğraf */}
                        <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-white aspect-[4/3]">
                            <ImageWithFallback
                                src={homepageImage}
                                alt="SDAL Kampüs"
                                className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105 object-bottom"
                            />
                        </div>
                        
                        {/* Modern Tasarımlara Özgü: Yüzen İstatistik / Bilgi Kartı */}
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8, duration: 0.5 }}
                            className="absolute -bottom-6 -left-6 md:-left-10 bg-white p-4 pr-6 rounded-2xl shadow-xl border border-slate-100 flex items-center gap-4"
                        >
                            <div className="bg-red-50 p-3 rounded-full text-[#A8322D]">
                                <Users className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Güçlü Bir Ağ</p>
                                <p className="text-xl font-bold text-slate-800">Yüzlerce Mezun</p>
                            </div>
                        </motion.div>
                    </motion.div>

                </div>
            </section>

            {/* --- ÖZELLİKLER / NEDEN BİZ? SECTION --- */}
            <section className="container mx-auto px-6 md:px-12 pt-10">
                <motion.div 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="text-center max-w-2xl mx-auto mb-12"
                >
                    <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">Neden Katılmalısın?</h2>
                    <p className="text-slate-500 text-lg">Lise yıllarındaki dayanışmayı profesyonel hayata taşıyoruz.</p>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-8">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
                        className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
                    >
                        <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <Users className="h-7 w-7 text-[#A8322D]" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-3">Güçlü Bir Ağ</h3>
                        <p className="text-slate-500 leading-relaxed">Farklı sektörlerdeki mezunlarımızla tanışın, iş ve sosyal hayatınızda kalıcı bağlar kurun.</p>
                    </motion.div>

                    <motion.div 
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
                        className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
                    >
                        <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <Handshake className="h-7 w-7 text-[#A8322D]" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-3">Kariyer & Mentorluk</h3>
                        <p className="text-slate-500 leading-relaxed">Tecrübeli mezunlarımızdan tavsiyeler alın veya genç mezunlarımıza yol gösterin.</p>
                    </motion.div>

                    <motion.div 
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}
                        className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
                    >
                        <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <Award className="h-7 w-7 text-[#A8322D]" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-3">Aidiyet Duygusu</h3>
                        <p className="text-slate-500 leading-relaxed">Okulumuzun değerlerini yaşatmaya devam edin ve ortak anılarınızı paylaşın.</p>
                    </motion.div>
                </div>
            </section>

            {/* --- MİSYON / HAKKIMIZDA KISA BİLGİ --- */}
            <motion.section 
                id="hakkimizda"
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.5, delay: 0.6 }}
                className="bg-slate-50 py-16 rounded-[3rem] mx-4 md:mx-12 px-6 md:px-12 border border-slate-100 mt-10"
            >
                <div className="flex flex-col md:flex-row items-center gap-12">
                    <div className="md:w-1/2">
                        <h2 className="text-3xl font-extrabold text-slate-900 mb-6 tracking-tight">Geçmişten Geleceğe Uzanan Köprü</h2>
                        <p className="text-slate-600 text-lg mb-8 leading-relaxed">
                            Amacımız, sıraları paylaştığımız dostlarımızla bağlarımızı taze tutmak ve bu güçlü sinerjiyi faydalı projelere dönüştürmektir. 
                        </p>
                        <ul className="space-y-4">
                            <li className="flex items-center gap-4 text-slate-700 font-medium bg-white p-3 rounded-xl shadow-sm">
                                <div className="w-2.5 h-2.5 rounded-full bg-[#A8322D]"></div>
                                Kültürel ve sosyal etkinlikler
                            </li>
                            <li className="flex items-center gap-4 text-slate-700 font-medium bg-white p-3 rounded-xl shadow-sm">
                                <div className="w-2.5 h-2.5 rounded-full bg-[#A8322D]"></div>
                                Mezun-Öğrenci buluşmaları
                            </li>
                            <li className="flex items-center gap-4 text-slate-700 font-medium bg-white p-3 rounded-xl shadow-sm">
                                <div className="w-2.5 h-2.5 rounded-full bg-[#A8322D]"></div>
                                Mesleki ağ oluşturma fırsatları
                            </li>
                        </ul>
                    </div>
                    <div className="md:w-1/2 flex justify-center w-full">
                        <div className="w-full max-w-md aspect-square md:aspect-[4/3] rounded-3xl overflow-hidden shadow-lg border-4 border-white relative">
                            {/* İkinci fotoğrafı da aynı src ile verdik, istersen değiştirebilirsin */}
                             <ImageWithFallback
                                src={homepageImage}
                                alt="Okul Bahçesi"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </motion.section>
        </div>
    );
}